import React from 'react';
import { SyncOperation } from '../types';

interface SyncOperationCardProps {
  operation: SyncOperation;
  onCancel: (syncId: string) => void;
}

export const SyncOperationCard: React.FC<SyncOperationCardProps> = ({ operation, onCancel }) => {
  const isActiveSync = (status: string) => {
    // Show cancel button for all active sync phases
    const activeStatuses = [
      'running',
      'initializing', 
      'fetching_events',
      'fetching_groups', 
      'fetching_orders',
      'syncing_to_hubspot',
      'creating_associations'
    ];
    return activeStatuses.includes(status);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
      case 'initializing':
      case 'fetching_events':
      case 'fetching_groups':
      case 'fetching_orders':
      case 'syncing_to_hubspot':
      case 'creating_associations':
        return '#3b82f6';
      case 'completed': return '#10b981';
      case 'failed': return '#ef4444';
      case 'cancelled': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
      case 'initializing':
      case 'fetching_events':
      case 'fetching_groups':
      case 'fetching_orders':
      case 'syncing_to_hubspot':
      case 'creating_associations':
        return '⏳';
      case 'completed': return '✅';
      case 'failed': return '❌';
      case 'cancelled': return '⏹️';
      default: return '❓';
    }
  };

  const formatDuration = (duration?: string | number) => {
    if (!duration) return 'N/A';

    // Handle both string and number (nanoseconds) formats
    if (typeof duration === 'number') {
      // Convert nanoseconds to human-readable format
      const totalSeconds = duration / 1_000_000_000;
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = Math.floor(totalSeconds % 60);

      const parts = [];
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}m`);
      if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
      return parts.join(' ');
    }

    // Parse Go duration format string (e.g., "1h23m45s")
    const match = duration.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+(?:\.\d+)?)s)?/);
    if (!match) return duration;

    const [, hours, minutes, seconds] = match;
    const parts = [];
    if (hours) parts.push(`${hours}h`);
    if (minutes) parts.push(`${minutes}m`);
    if (seconds) parts.push(`${Math.round(parseFloat(seconds))}s`);
    return parts.join(' ') || '0s';
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="sync-operation-card">
      <div className="card-header">
        <div className="operation-title">
          <span className="status-icon">{getStatusIcon(operation.status)}</span>
          <span className="operation-id">{operation.id}</span>
        </div>
        <div className="operation-actions">
          {isActiveSync(operation.status) && (
            <button
              onClick={() => onCancel(operation.id)}
              className="cancel-button"
              title="Cancel sync"
            >
              <span>✕</span>
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="card-content">
        <div className="operation-info">
          <div className="info-row">
            <span className="label">Type:</span>
            <span className="value">{operation.type}</span>
          </div>
          <div className="info-row">
            <span className="label">Status:</span>
            <span
              className="value status-badge"
              style={{ backgroundColor: getStatusColor(operation.status) }}
            >
              {operation.status}
            </span>
          </div>
          <div className="info-row">
            <span className="label">Started:</span>
            <span className="value">{formatDate(operation.startedAt)}</span>
          </div>
          <div className="info-row">
            <span className="label">Updated:</span>
            <span className="value">{formatDate(operation.updatedAt)}</span>
          </div>
        </div>

        {operation.currentOperation && (
          <div className="current-operation">
            <span className="label">Current:</span>
            <span className="value">{operation.currentOperation}</span>
          </div>
        )}

        <div className="progress-section">
          <div className="progress-info">
            <span>{operation.progress} / {operation.total}</span>
            <span>{operation.percentComplete.toFixed(1)}%</span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${operation.percentComplete}%` }}
            />
          </div>
        </div>

        {operation.processingMetrics && (
          <div className="metrics-section">
            <h4>Processing Metrics</h4>
            <div className="metrics-grid">
              <div className="metric">
                <span className="metric-label">Items/sec:</span>
                <span className="metric-value">{operation.processingMetrics.itemsPerSecond.toFixed(2)}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Errors:</span>
                <span className="metric-value">{operation.processingMetrics.errorCount}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Avg Latency:</span>
                <span className="metric-value">{formatDuration(operation.processingMetrics.averageLatency)}</span>
              </div>
              {operation.processingMetrics.estimatedTimeLeft && (
                <div className="metric">
                  <span className="metric-label">ETA:</span>
                  <span className="metric-value">{formatDuration(operation.processingMetrics.estimatedTimeLeft)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {operation.batchInfo && (operation.batchInfo.currentEntityType || operation.batchInfo.totalBatches) && (
          <div className="batch-section">
            <h4>Batch Progress</h4>
            <div className="batch-grid">
              {operation.batchInfo.currentEntityType && (
                <div className="batch-item">
                  <span className="label">Processing:</span>
                  <span className="value">{operation.batchInfo.currentEntityType}</span>
                </div>
              )}
              {operation.batchInfo.currentBatch && operation.batchInfo.totalBatches && (
                <div className="batch-item">
                  <span className="label">Batch:</span>
                  <span className="value">{operation.batchInfo.currentBatch} / {operation.batchInfo.totalBatches}</span>
                </div>
              )}
              {operation.batchInfo.batchSize && (
                <div className="batch-item">
                  <span className="label">Batch Size:</span>
                  <span className="value">{operation.batchInfo.batchSize}</span>
                </div>
              )}
              {operation.batchInfo.currentBatch && operation.batchInfo.totalBatches && (
                <div className="batch-item">
                  <span className="label">Batch Progress:</span>
                  <span className="value">
                    {((operation.batchInfo.currentBatch / operation.batchInfo.totalBatches) * 100).toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {operation.concurrencyInfo && (
          <div className="concurrency-section">
            <h4>Concurrency Info</h4>
            <div className="concurrency-grid">
              <div className="concurrency-item">
                <span className="label">Brushfire Workers:</span>
                <span className="value">{operation.concurrencyInfo.brushfireWorkerPool}</span>
              </div>
              <div className="concurrency-item">
                <span className="label">Hubspot Workers:</span>
                <span className="value">{operation.concurrencyInfo.hubspotWorkerPool}</span>
              </div>
              <div className="concurrency-item">
                <span className="label">Active Workers:</span>
                <span className="value">{operation.concurrencyInfo.activeWorkers}</span>
              </div>
              <div className="concurrency-item">
                <span className="label">Queued Tasks:</span>
                <span className="value">{operation.concurrencyInfo.queuedTasks}</span>
              </div>
              {operation.concurrencyInfo.rateLimitStatus && (
                <>
                  <div className="concurrency-item">
                    <span className="label">Rate Limit:</span>
                    <span className="value">
                      {operation.concurrencyInfo.rateLimitStatus.remaining} / {operation.concurrencyInfo.rateLimitStatus.limit}
                    </span>
                  </div>
                  {operation.concurrencyInfo.rateLimitStatus.throttleActive && (
                    <div className="concurrency-item">
                      <span className="label">Throttled:</span>
                      <span className="value throttled">Yes</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {operation.error && (
          <div className="error-section">
            <h4>Error</h4>
            <div className="error-message">{operation.error}</div>
          </div>
        )}
      </div>
    </div>
  );
};
